import { createAction } from "@reduxjs/toolkit";
import _ from "lodash";
import axios from "axios";
import { takeEvery, call, put, select } from "redux-saga/effects";
import buildUrl from "build-url-ts";
import { 
  CREATE_SIMULATION_REQUEST, 
  CREATING_COURSE_FAILED, 
  CREATING_SIMULATION_EDITOR_CHAT_FAILED, 
  CREATING_SIMULATION_EDITOR_CHAT_REQUEST, 
  CREATING_SIMULATION_EDITOR_CHAT_SUCCEEDED, 
  CREATING_SIMULATION_SUCCEEDED, 
  DELETE_SIMULATION_REQUEST, 
  DELETING_SIMULATION_FAILED, 
  DELETING_SIMULATION_SUCCEEDED, 
  DISCARD_SIMULATION_DRAFT_REQUEST, 
  DISCARDING_SIMULATION_DRAFT_FAILED, 
  DISCARDING_SIMULATION_DRAFT_SUCCEEDED, 
  EDIT_SIMULATION_REQUEST, 
  FETCH_DRAFT_SIMULATION_REQUEST, 
  FETCH_PUBLIC_SIMULATION_REQUEST, 
  FETCHING_DRAFT_SIMULATION_FAILED,
  FETCHING_DRAFT_SIMULATION_SUCCEEDED, 
  FETCHING_PERSONALITIES_FOR_SIMULATION_FAILED, 
  FETCHING_PERSONALITIES_FOR_SIMULATION_REQUEST, 
  FETCHING_PERSONALITIES_FOR_SIMULATION_SUCCEEDED, 
  FETCHING_PUBLIC_SIMULATION_FAILED, 
  FETCHING_PUBLIC_SIMULATION_SUCCEEDED, 
  FETCHING_VOICES_FOR_SIMULATION_FAILED, 
  FETCHING_VOICES_FOR_SIMULATION_REQUEST, 
  FETCHING_VOICES_FOR_SIMULATION_SUCCEEDED, 
  GENERATING_SUMMARY_FOR_SIMULATION_FAILED, 
  GENERATING_SUMMARY_FOR_SIMULATION_REQUEST, 
  GENERATING_SUMMARY_FOR_SIMULATION_SUCCEEDED, 
  INAVLIDATE_GENERATE_SUMMARY_FOR_SIMULATION, 
  INVALIDATE_SHUFFLE_OBJECTIVES_STATE, 
  INVALIDATE_SHUFFLE_ORGANIZATION_STATE, 
  INVALIDATE_SHUFFLE_PERSONA_STATE, 
  INVALIDATE_SIMULATION_CREATION_STATE, 
  INVALIDATE_SIMULATION_DELETION_STATE, 
  INVALIDATE_SIMULATION_UPDATE_STATE, 
  PUBLISHING_SIMULATION_FAILED, 
  PUBLISHING_SIMULATION_REQUEST, 
  PUBLISHING_SIMULATION_SUCCEEDED, 
  SHUFFLING_OBJECTIVES_FOR_SIMULATION_FAILED, 
  SHUFFLING_OBJECTIVES_FOR_SIMULATION_REQUEST, 
  SHUFFLING_OBJECTIVES_FOR_SIMULATION_SUCCEEDED, 
  SHUFFLING_ORGANIZATION_FOR_SIMULATION_FAILED, 
  SHUFFLING_ORGANIZATION_FOR_SIMULATION_REQUEST, 
  SHUFFLING_ORGANIZATION_FOR_SIMULATION_SUCCEEDED, 
  SHUFFLING_PERSONA_FOR_SIMULATION_FAILED, 
  SHUFFLING_PERSONA_FOR_SIMULATION_REQUEST, 
  SHUFFLING_PERSONA_FOR_SIMULATION_SUCCEEDED, 
  UPDATE_SIMULATION_PROPERTY_REQUEST, 
  UPDATE_VALIDATION_ERRORS_FOR_SIMULATION, 
  UPDATING_SIMULATION_FAILED, 
  UPDATING_SIMULATION_REQUEST, 
  UPDATING_SIMULATION_SUCCEEDED, 
  VALIDATE_SIMULATION_REQUEST 
} from "../actionTypes";

import { 
  CreateSimulationEditorChatErrorResponse, 
  CreateSimulationEditorChatRequest, 
  CreateSimulationEditorChatResponse, 
  CreateSimulationErrorResponse, 
  CreateSimulationRequest, 
  CreateSimulationResponse, 
  DeleteSimulationErrorResponse, 
  DeleteSimulationResponse, 
  DiscardSimulationDraftErrorResponse, 
  DiscardSimulationDraftResponse, 
  FetchPersonalitiesErrorResponse, 
  FetchPersonalitiesResponse, 
  FetchPublicSimulationErrorResponse, 
  FetchPublicSimulationRequest, 
  FetchPublicSimulationResponse, 
  FetchSimulationResponse, 
  FetchVoicesErrorResponse, 
  FetchVoicesRequest, 
  FetchVoicesResponse, 
  GenerateSimulationSummaryErrorResponse, 
  GenerateSimulationSummaryRequest, 
  GenerateSimulationSummaryResponse, 
  PublishSimulationErrorResponse, 
  PublishSimulationResponse, 
  ShuffleObjectivesErrorResponse, 
  ShuffleObjectivesResponse, 
  ShuffleOrganizationErrorResponse, 
  ShuffleOrganizationResponse, 
  ShufflePersonaErrorResponse, 
  ShufflePersonaResponse, 
  Simulation, 
  UpdateSimulationErrorResponse, 
  UpdateSimulationPropertyRequest, 
  UpdateSimulationRequest, 
  UpdateSimulationResponse, 
  UpdateValidationErrorsForSimulationRequest 
} from "src/types/coaching/simulation";

import { FetchScenarioErrorResponse } from "src/types/simulation/scenarios";
import { validateSimulationRequest } from "src/pages/coaching/manage/simulations/edit/helper";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_COACHING_API_URL}`;

// create Simulation
export const createSimulation = createAction<CreateSimulationRequest>(CREATE_SIMULATION_REQUEST);
export const creatingSimulationSucceeded = createAction<CreateSimulationResponse>(CREATING_SIMULATION_SUCCEEDED);
const creatingSimulationFailed = createAction<CreateSimulationErrorResponse>(CREATING_COURSE_FAILED);

const createSimulationSaga = function* (action: ReturnType<typeof createSimulation>): any {
  const request = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations`,
  });

  const createCourseApi = () => {
    return axios({
      method: "POST",
      url: url,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(createCourseApi);
    yield put(creatingSimulationSucceeded({ response: response }));
  } catch (error) {
    yield put(creatingSimulationFailed({ error: error }));
  }
};

export const invalidateSimulationCreationState = createAction<void>(INVALIDATE_SIMULATION_CREATION_STATE);

// fetch draft simulation
export const fetchDraftSimulation = createAction<string>(FETCH_DRAFT_SIMULATION_REQUEST);
const fetchingDraftSimulationSucceeded = createAction<FetchSimulationResponse>(FETCHING_DRAFT_SIMULATION_SUCCEEDED);
const fetchingDraftSimulationFailed = createAction<FetchScenarioErrorResponse>(FETCHING_DRAFT_SIMULATION_FAILED);

const fetchDraftSimulationSaga = function* (action: ReturnType<typeof fetchDraftSimulation>): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}/draft`,
  });

  const fetchSimulationApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSimulationApi);
    yield put(fetchingDraftSimulationSucceeded({
      response: response,
    }));
  } catch (error) {
    yield put(fetchingDraftSimulationFailed({ id: id, error: error }));
  }
};

//Edit simulation
export const editSimulation = createAction<Simulation>(EDIT_SIMULATION_REQUEST);

//Validate simulation
export const validateSimulation = createAction<string>(VALIDATE_SIMULATION_REQUEST);
export const updateValidationErrorsSimulation = createAction<UpdateValidationErrorsForSimulationRequest>(UPDATE_VALIDATION_ERRORS_FOR_SIMULATION);

const validateSimulationSaga = function* (action: ReturnType<typeof validateSimulation>): any {
  const id = action.payload;
  const simulation = yield select((state) => state.coaching.simulations.edit?.item);

  if (simulation && simulation.id === id) {
    const errors = validateSimulationRequest(simulation);
    yield put(
      updateValidationErrorsSimulation({ simulation: simulation.id!, errors: errors }),
    );
  }
};

export const updateSimulationProperty = createAction<UpdateSimulationPropertyRequest>(UPDATE_SIMULATION_PROPERTY_REQUEST);

//Update Simulation
export const updateSimulation = createAction<UpdateSimulationRequest>(UPDATING_SIMULATION_REQUEST);
const updatingSimulationSucceeded = createAction<UpdateSimulationResponse>(UPDATING_SIMULATION_SUCCEEDED);
const updatingSimulationFailed = createAction<UpdateSimulationErrorResponse>(UPDATING_SIMULATION_FAILED);

const updateSimulationSaga = function* (
  action: ReturnType<typeof updateSimulation>,
): any {
  const { id, request } = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}`,
  });

  const updateCourseApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: request,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateCourseApi);
    yield put(updatingSimulationSucceeded({ response: response }));
  } catch (error) {
    yield put(updatingSimulationFailed({ id: id, error: error }));
  }
};

export const invalidateSimulationUpdateState = createAction<void>(INVALIDATE_SIMULATION_UPDATE_STATE);

// publish simulation
export const publishSimulation = createAction<string>(PUBLISHING_SIMULATION_REQUEST);
const publishingSimulationSucceeded = createAction<PublishSimulationResponse>(PUBLISHING_SIMULATION_SUCCEEDED);
const publishingSimulationFailed = createAction<PublishSimulationErrorResponse>(PUBLISHING_SIMULATION_FAILED);

const publishSimulationSaga = function* (
  action: ReturnType<typeof publishSimulation>,
): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}/publish`,
  });

  const publishSimulationApi = () => {
    return axios({
      method: "POST",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(publishSimulationApi);
    yield put(publishingSimulationSucceeded({ response: response }));
  } catch (error) {
    yield put(publishingSimulationFailed({ id: id, error: error }));
  }
};

//create simulation editor chat
export const createSimulationEditorChat = createAction<CreateSimulationEditorChatRequest>(CREATING_SIMULATION_EDITOR_CHAT_REQUEST);
const creatingSimulationEditorChatSucceeded = createAction<CreateSimulationEditorChatResponse>(CREATING_SIMULATION_EDITOR_CHAT_SUCCEEDED);
const creatingSimulationEditorChatFailed = createAction<CreateSimulationEditorChatErrorResponse>(CREATING_SIMULATION_EDITOR_CHAT_FAILED);

const createSimulationEditorChatSaga = function* (action: ReturnType<typeof createSimulationEditorChat>): any {
  const request = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations_editor/${request.simulation}`,
  });

  const data = {
    session: request.session,
    configurationType: request.configurationType,
    input: request.input,
    parameters: request.parameters,
  }

  const createSimulationEditorChatApi = () => {
    return axios({
      method: "POST",
      url: url,
      data: data,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(createSimulationEditorChatApi);
    yield put(creatingSimulationEditorChatSucceeded({
      simulation: request.simulation,
      session: response.session,
      configurationType: response.configurationType,
      input: response.input,
      output: response.output,
      parameters: response.parameters,
    }));
  } catch (error) {
    yield put(creatingSimulationEditorChatFailed({
      simulation: request.simulation,
      session: request.session,
      configurationType: request.configurationType,
      error: error,
    }));
  }
};

export const generateSimulationSummary = createAction<GenerateSimulationSummaryRequest>(GENERATING_SUMMARY_FOR_SIMULATION_REQUEST);
const generatingSimulationSummarySucceeded = createAction<GenerateSimulationSummaryResponse>(GENERATING_SUMMARY_FOR_SIMULATION_SUCCEEDED);
const generatingSimulationSummaryFailed = createAction<GenerateSimulationSummaryErrorResponse>(GENERATING_SUMMARY_FOR_SIMULATION_FAILED);

const generateSimulationSummarySaga = function* (action: ReturnType<typeof generateSimulationSummary>): any {
  const request = action.payload as GenerateSimulationSummaryRequest;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations_editor/${request.simulation}/summary`,
  });

  const data = {
    description: request.description,
    objectives: request.objectives,
    persona: request.persona,
    organization: request.organization,
  }

  const generateSimulationSummaryApi = () => {
    return axios({
      method: "POST",
      url: url,
      data: data,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(generateSimulationSummaryApi);
    yield put(generatingSimulationSummarySucceeded({
      simulation: request.simulation,
      response: response,
    }));
  } catch (error) {
    yield put(generatingSimulationSummaryFailed({
      simulation: request.simulation,
      error: error,
    }));
  }
};

export const invalidateGenerateSimulationSummaryState = createAction<string>(INAVLIDATE_GENERATE_SUMMARY_FOR_SIMULATION);

// Fetch personalities
export const fetchPersonalities = createAction<void>(FETCHING_PERSONALITIES_FOR_SIMULATION_REQUEST);
const fetchingPersonalitiesSucceeded = createAction<FetchPersonalitiesResponse>(FETCHING_PERSONALITIES_FOR_SIMULATION_SUCCEEDED);
const fetchingPersonalitiesFailed = createAction<FetchPersonalitiesErrorResponse>(FETCHING_PERSONALITIES_FOR_SIMULATION_FAILED);

const fetchPersonalitiesSaga = function* (): any {
  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/personalities`,
  });

  const fetchPersonalitiesApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPersonalitiesApi);
    yield put(fetchingPersonalitiesSucceeded({
      response: response,
    }));
  } catch (error) {
    yield put(fetchingPersonalitiesFailed({ error: error }));
  }
}

// Fetch voices
export const fetchVoices = createAction<FetchVoicesRequest>(FETCHING_VOICES_FOR_SIMULATION_REQUEST);
const fetchingVoicesSucceeded = createAction<FetchVoicesResponse>(FETCHING_VOICES_FOR_SIMULATION_SUCCEEDED);
const fetchingVoicesFailed = createAction<FetchVoicesErrorResponse>(FETCHING_VOICES_FOR_SIMULATION_FAILED);

const fetchVoicesSaga = function* (action: ReturnType<typeof fetchVoices>): any {
  const { page, limit } = action.payload as FetchVoicesRequest;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/voices`,
    queryParams: {
      page: page,
      limit: limit,
    },
  });

  const fetchPersonalitiesApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPersonalitiesApi);
    yield put(fetchingVoicesSucceeded({
      response: response,
    }));
  } catch (error) {
    yield put(fetchingVoicesFailed({ error: error }));
  }
}

//Shuffle persona for simulation
export const shufflePersona = createAction<string>(SHUFFLING_PERSONA_FOR_SIMULATION_REQUEST);
const shufflingPersonaSucceeded = createAction<ShufflePersonaResponse>(SHUFFLING_PERSONA_FOR_SIMULATION_SUCCEEDED);
const shufflingPersonaFailed = createAction<ShufflePersonaErrorResponse>(SHUFFLING_PERSONA_FOR_SIMULATION_FAILED);

const shufflePersonaSaga = function* (action: ReturnType<typeof shufflePersona>): any {
  const id = action.payload;
  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}/persona/shuffle`
  });

  const shufflePersonaApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(shufflePersonaApi);
    yield put(shufflingPersonaSucceeded({
      id: id,
      response: response,
    }));
  } catch (error) {
    yield put(shufflingPersonaFailed({ id: id, error: error }));
  }
}

export const invalidateShufflePersonaState = createAction<string>(INVALIDATE_SHUFFLE_PERSONA_STATE);

//Shuffle organization for simulation
export const shuffleOrganization = createAction<string>(SHUFFLING_ORGANIZATION_FOR_SIMULATION_REQUEST);
const shufflingOrganizationSucceeded = createAction<ShuffleOrganizationResponse>(SHUFFLING_ORGANIZATION_FOR_SIMULATION_SUCCEEDED);
const shufflingOrganizationFailed = createAction<ShuffleOrganizationErrorResponse>(SHUFFLING_ORGANIZATION_FOR_SIMULATION_FAILED);

const shuffleOrganizationSaga = function* (action: ReturnType<typeof shuffleOrganization>): any {
  const id = action.payload;
  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}/organization/shuffle`
  });

  const shuffleOrganizationApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(shuffleOrganizationApi);
    yield put(shufflingOrganizationSucceeded({
      id: id,
      response: response,
    }));
  } catch (error) {
    yield put(shufflingOrganizationFailed({ id: id, error: error }));
  }
}

export const invalidateShuffleOrganizationState = createAction<string>(INVALIDATE_SHUFFLE_ORGANIZATION_STATE);

//Shuffle objectives for simulation
export const shuffleObjectives = createAction<string>(SHUFFLING_OBJECTIVES_FOR_SIMULATION_REQUEST);
const shufflingObjectivesSucceeded = createAction<ShuffleObjectivesResponse>(SHUFFLING_OBJECTIVES_FOR_SIMULATION_SUCCEEDED);
const shufflingObjectivesFailed = createAction<ShuffleObjectivesErrorResponse>(SHUFFLING_OBJECTIVES_FOR_SIMULATION_FAILED);

const shuffleObjectivesSaga = function* (action: ReturnType<typeof shuffleObjectives>): any {
  const id = action.payload;
  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}/objectives/shuffle`
  });

  const shuffleObjectivesApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(shuffleObjectivesApi);
    yield put(shufflingObjectivesSucceeded({
      id: id,
      response: response,
    }));
  } catch (error) {
    yield put(shufflingObjectivesFailed({ id: id, error: error }));
  }
}

export const invalidateShuffleObjectivesState = createAction<string>(INVALIDATE_SHUFFLE_OBJECTIVES_STATE);

// delete simulation
export const deleteSimulation = createAction<string>(DELETE_SIMULATION_REQUEST);
const deletingSimulationSucceeded = createAction<DeleteSimulationResponse>(DELETING_SIMULATION_SUCCEEDED);
const deletingSimulationFailed = createAction<DeleteSimulationErrorResponse>(DELETING_SIMULATION_FAILED);

const deleteSimulationSaga = function* (action: ReturnType<typeof deleteSimulation>): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}`,
  });

  const deleteSimulationApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(deleteSimulationApi);
    yield put(deletingSimulationSucceeded({ id: id, response: response }));
  } catch (error) {
    yield put(deletingSimulationFailed({ id: id, error: error }));
  }
};

export const invalidateSimulationDeletionState = createAction<string>(INVALIDATE_SIMULATION_DELETION_STATE);

// discard simulation draft
export const discardSimulationDraft = createAction<string>(DISCARD_SIMULATION_DRAFT_REQUEST);
const discardingSimulationDraftSucceeded = createAction<DiscardSimulationDraftResponse>(DISCARDING_SIMULATION_DRAFT_SUCCEEDED);
const discardingSimulationDraftFailed = createAction<DiscardSimulationDraftErrorResponse>(DISCARDING_SIMULATION_DRAFT_FAILED);

const discardSimulationDraftSaga = function* (action: ReturnType<typeof discardSimulationDraft>): any {
  const id = action.payload;

  const url = buildUrl(apiUrl, {
    path: `api/1.0/simulations/${id}/draft`,
  });

  const discardSimulationApi = () => {
    return axios({
      method: "DELETE",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(discardSimulationApi);
    yield put(discardingSimulationDraftSucceeded({ response: response }));
  } catch (error) {
    yield put(discardingSimulationDraftFailed({ id: id, error: error }));
  }
};

export const fetchPublicSimulation = createAction<FetchPublicSimulationRequest>(FETCH_PUBLIC_SIMULATION_REQUEST);

const fetchPublicSimualtionSucceeded = createAction<FetchPublicSimulationResponse>(FETCHING_PUBLIC_SIMULATION_SUCCEEDED);
const fetchPublicSimualtionFailed = createAction<FetchPublicSimulationErrorResponse>(FETCHING_PUBLIC_SIMULATION_FAILED);

const fetchPublicSimualtionSaga = function* (action: ReturnType<typeof fetchPublicSimulation>): any {
  const request = action.payload as FetchPublicSimulationRequest;
  const id = request.id;

  const fetchPublicSimualtionApi = () => {
    return axios({
      method: "GET",
      url: `${apiUrl}/api/1.0/simulations/public/${id}`,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchPublicSimualtionApi);
    yield put(
      fetchPublicSimualtionSucceeded({
        simulation: response.simulation,
        learningRecord: response.learningRecord
      }),
    );
  } catch (error) {
    yield put(fetchPublicSimualtionFailed({id: id, error: error }));
  }
};

export default function* simulationSaga() {
  yield takeEvery(CREATE_SIMULATION_REQUEST, createSimulationSaga);
  yield takeEvery(FETCH_DRAFT_SIMULATION_REQUEST, fetchDraftSimulationSaga);
  yield takeEvery(VALIDATE_SIMULATION_REQUEST, validateSimulationSaga);
  yield takeEvery(UPDATING_SIMULATION_REQUEST, updateSimulationSaga);
  yield takeEvery(PUBLISHING_SIMULATION_REQUEST, publishSimulationSaga);
  yield takeEvery(CREATING_SIMULATION_EDITOR_CHAT_REQUEST, createSimulationEditorChatSaga);
  yield takeEvery(GENERATING_SUMMARY_FOR_SIMULATION_REQUEST, generateSimulationSummarySaga);

  yield takeEvery(FETCHING_PERSONALITIES_FOR_SIMULATION_REQUEST, fetchPersonalitiesSaga);
  yield takeEvery(FETCHING_VOICES_FOR_SIMULATION_REQUEST, fetchVoicesSaga);

  yield takeEvery(SHUFFLING_PERSONA_FOR_SIMULATION_REQUEST, shufflePersonaSaga);
  yield takeEvery(SHUFFLING_ORGANIZATION_FOR_SIMULATION_REQUEST, shuffleOrganizationSaga);
  yield takeEvery(SHUFFLING_OBJECTIVES_FOR_SIMULATION_REQUEST, shuffleObjectivesSaga);

  yield takeEvery(DELETE_SIMULATION_REQUEST, deleteSimulationSaga);
  yield takeEvery(DISCARD_SIMULATION_DRAFT_REQUEST, discardSimulationDraftSaga);
  yield takeEvery(FETCH_PUBLIC_SIMULATION_REQUEST, fetchPublicSimualtionSaga);

}